import {Link} from "react-router-dom";
import {SectionHeader} from "./Shared";
import {useGetMarketplaceCategoriesQuery} from "../../store/services/publicApi";

/**
 * Photos and icons stay in the page. Names and descriptions come from
 * MarketplaceCategory when the API is available.
 */
const CATEGORY_VISUALS: Record<string, {photo: string; icon: string; badge: string}> = {
    "Solar and Energy Storage": {photo: "/public/sections/cat_solar_storage.jpg", icon: "fas fa-sun", badge: "#1A822A"},
    "Electrical Equipment": {photo: "/public/sections/cat_electrical.jpg", icon: "fas fa-bolt", badge: "#F26722"},
    "Energy Service": {photo: "/public/sections/cat_services.jpg", icon: "fas fa-tint", badge: "#2E9BDE"},
    "Productive Use Equipments": {photo: "/public/sections/cat_productive.jpg", icon: "fas fa-leaf", badge: "#3FA34D"},
};

const FALLBACK_VISUAL = {photo: "/public/sections/cat_solar_storage.jpg", icon: "fas fa-leaf", badge: "#1A822A"};

type CategoryCard = {
    id?: string;
    name: string;
    description?: string;
    photo: string;
    icon: string;
    badge: string;
};

const CATEGORIES: CategoryCard[] = [
    {
        name: "Solar and Energy Storage",
        description: "Solar panels, inverters, batteries, solar kits and more.",
        photo: "/public/sections/cat_solar_storage.jpg",
        icon: "fas fa-sun",
        badge: "#1A822A",
    },
    {
        name: "Electrical Equipment",
        description: "Cables, switchgear, transformers, meters, panels and more.",
        photo: "/public/sections/cat_electrical.jpg",
        icon: "fas fa-bolt",
        badge: "#F26722",
    },
    {
        name: "Energy Service",
        description: "Installation, O&M, consulting, training and technical services.",
        photo: "/public/sections/cat_services.jpg",
        icon: "fas fa-tint",
        badge: "#2E9BDE",
    },
    {
        name: "Productive Use Equipments",
        description: "Solar pumps, dryers, cold rooms, machinery and more.",
        photo: "/public/sections/cat_productive.jpg",
        icon: "fas fa-leaf",
        badge: "#3FA34D",
    },
];

export const EnergyMarketplaceCategories = () => {
    const {data} = useGetMarketplaceCategoriesQuery({homepageOnly: true});
    const categories: CategoryCard[] = data?.length
        ? data.map((category: {id: string; name: string; description?: string}) => ({
            id: category.id,
            name: category.name,
            description: category.description,
            ...(CATEGORY_VISUALS[category.name] || FALLBACK_VISUAL),
        }))
        : CATEGORIES;

    return (
        <section className="oec-section">
            <div className="oec-section-inner">
                <SectionHeader
                    title="Energy Marketplace"
                    subtitle="Find trusted suppliers and quality products for your energy projects and operations."
                    linkLabel="View All Products"
                    linkTo="/marketplace"
                />

                <div className="oec-card-grid oec-card-grid-roomy">
                    {categories.map((category) => (
                        <Link
                            key={category.id || category.name}
                            to={category.id ? `/marketplace?categoryId=${category.id}` : "/marketplace"}
                            className="oec-cat-card"
                        >
                            <div className="oec-cat-card-media">
                                <img src={category.photo} alt={category.name} loading="lazy"/>
                                <span className="oec-cat-card-badge" style={{backgroundColor: category.badge}}>
                                    <i className={category.icon}/>
                                </span>
                            </div>
                            <div className="oec-cat-card-body">
                                <h3 className="oec-cat-card-title">{category.name}</h3>
                                <div className="oec-cat-card-footer">
                                    <p className="oec-cat-card-text">{category.description}</p>
                                    <span className="oec-cat-card-go" aria-hidden="true">
                                        <i className="fas fa-arrow-right"/>
                                    </span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
};
